/**
 * Kokoro TTS - on-device only via WebGPU (iOS 26+, modern Chrome/Edge).
 *
 * Model weights from HF CDN (static files). Cached in browser after first
 * download - works fully offline on subsequent plays.
 */

import { speakOnDevice, loadOnDeviceKokoro, type LoadCallback } from './kokoro-ondevice';
import { speakViaServer, serverHealth, getServerUrl, abortServerPlayback } from './kokoro-server';

export type KokoroEngine = 'server' | 'ondevice';

function getEngineChoice(): KokoroEngine {
  const override = (new URLSearchParams(location.search).get('engine') || localStorage.getItem('kokoroEngine') || '').trim();
  if (override === 'ondevice' || override === 'server') return override;
  return 'server';
}

let audioCtx: AudioContext | null = null;
let session = 0;

export type KokoroVoice =
  | 'af_bella' | 'am_michael' | 'af_nicole' | 'af_sarah'
  | 'am_adam' | 'bf_emma' | 'af_heart' | 'bm_george';

export type LoadProgress = {
  status: 'init' | 'downloading' | 'ready' | 'error';
  pct: number;
  detail?: string;
};

export type SpeakOpts = {
  voice: KokoroVoice;
  speed?: number;
  onLoad?: (p: LoadProgress) => void;
  onSentence?: (idx: number, total: number) => void;
  onEnd?: () => void;
  /** Book context for offline cache lookup. When set, server engine
   *  checks IndexedDB cache before hitting the laptop /tts endpoint. */
  bookId?: string;
  chapterIdx?: number;
  sentenceStartIdx?: number;
};

function log(...args: any[]) {
  console.log('[kokoro]', ...args);
  try { (window as any).__doomLog?.('[kokoro] ' + args.map((a) => typeof a === 'string' ? a : JSON.stringify(a)).join(' ')); } catch {}
}

function splitSentencesInternal(text: string): string[] {
  return text.replace(/\s+/g, ' ').split(/(?<=[.!?])\s+(?=[A-Z"\u2018\u201C])/).map((s) => s.trim()).filter(Boolean);
}

/** MUST be called inside a user click/tap handler to unlock iOS audio. */
export function ensureAudioFromGesture() {
  try {
    if ('audioSession' in navigator) {
      (navigator as any).audioSession.type = 'playback';
    }
  } catch (e) { log('audioSession', e); }

  if (!audioCtx) {
    const Ctor: any = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return;
    try { audioCtx = new Ctor({ sampleRate: 24000 }); }
    catch { audioCtx = new Ctor(); }
    log('AudioContext created, state:', audioCtx?.state, 'rate:', audioCtx?.sampleRate);
  }
  const ctx = audioCtx;
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  try {
    const buf = ctx.createBuffer(1, 1, ctx.sampleRate);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start(0);
  } catch {}
}

export async function playTestBeep() {
  ensureAudioFromGesture();
  const ctx = audioCtx;
  if (!ctx) throw new Error('no AudioContext');
  if (ctx.state === 'suspended') await ctx.resume();
  const duration = 0.6;
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * duration), ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    const t = i / ctx.sampleRate;
    data[i] = Math.sin(2 * Math.PI * 440 * t) * 0.3 * Math.max(0, 1 - t / duration);
  }
  await new Promise<void>((resolve) => {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.onended = () => resolve();
    src.start(0);
  });
}

/** Pre-load on-device model. Call early to avoid delay on first play. */
export async function loadKokoro(onProgress?: (p: LoadProgress) => void): Promise<void> {
  if (getEngineChoice() === 'server') {
    const h = await serverHealth();
    if (h.ok) { onProgress?.({ status: 'ready', pct: 100, detail: 'Server reachable' }); return; }
    // Server unreachable - auto-fallback to on-device
    log('Server unreachable, falling back to on-device. Detail:', h.detail);
    onProgress?.({ status: 'init', pct: 0, detail: 'Server offline, loading on-device voice...' });
  }
  await loadOnDeviceKokoro(onProgress as LoadCallback);
}

export function getActiveEngine(): KokoroEngine { return getEngineChoice(); }

/**
 * Speak text or a pre-split sentence array via on-device Kokoro WebGPU.
 */
export async function speakKokoro(textOrSentences: string | string[], opts: SpeakOpts) {
  log('speakKokoro start, voice=' + opts.voice + ' speed=' + opts.speed);
  opts.onLoad?.({ status: 'init', pct: 0, detail: 'Starting voice...' });
  stopKokoro();
  const mySession = ++session;
  ensureAudioFromGesture();

  const ctx = audioCtx;
  if (!ctx) {
    log('No AudioContext');
    opts.onLoad?.({ status: 'error', pct: 0, detail: 'No AudioContext' });
    opts.onEnd?.();
    return;
  }
  log('ctx state before resume=' + ctx.state);
  if (ctx.state === 'suspended') {
    try { await ctx.resume(); log('ctx resumed, state=' + ctx.state); }
    catch (e: any) { log('ctx resume threw: ' + (e?.message || e)); }
  }

  const sentences = Array.isArray(textOrSentences)
    ? textOrSentences.filter((s) => s && s.trim().length)
    : splitSentencesInternal(textOrSentences);
  if (!sentences.length) {
    log('no sentences');
    opts.onLoad?.({ status: 'error', pct: 0, detail: 'No sentences' });
    opts.onEnd?.();
    return;
  }
  log('sentences=' + sentences.length);

  const engine = getEngineChoice();
  log('engine=' + engine + (engine === 'server' ? ' url=' + getServerUrl() : ''));

  if (engine === 'server') {
    try {
      await speakViaServer(sentences, {
        voice: opts.voice,
        speed: opts.speed,
        audioCtx: ctx,
        onLoad: (p) => { if (p.status !== 'downloading') log('load ' + p.status); opts.onLoad?.(p); },
        onSentence: opts.onSentence,
        onEnd: opts.onEnd,
        isActive: () => mySession === session,
        bookId: opts.bookId,
        chapterIdx: opts.chapterIdx,
        sentenceStartIdx: opts.sentenceStartIdx,
      });
      log('speakViaServer returned');
      return;
    } catch (e: any) {
      log('speakViaServer threw, falling back to on-device: ' + (e?.message || e));
      opts.onLoad?.({ status: 'init', pct: 0, detail: 'Server offline, switching to on-device...' });
      // Fall through to on-device below
    }
  }

  try {
    await speakOnDevice(sentences, {
      voice: opts.voice,
      speed: opts.speed,
      audioCtx: ctx,
      onLoad: (p) => {
        if (p.status !== 'downloading') log('load ' + p.status + (p.detail ? ' ' + String(p.detail).slice(0, 80) : ''));
        opts.onLoad?.(p);
      },
      onSentence: opts.onSentence,
      onEnd: opts.onEnd,
      isActive: () => mySession === session,
    });
    log('speakOnDevice returned');
  } catch (e: any) {
    log('speakOnDevice threw: ' + (e?.message || e));
    opts.onLoad?.({ status: 'error', pct: 0, detail: String(e?.message || e) });
    opts.onEnd?.();
  }
}

/** Re-exported for Settings/diagnostic UIs. */
export { serverHealth, getServerUrl } from './kokoro-server';

export function stopKokoro() {
  session++;
  // Hard-stop any in-flight server playback: abort pending fetches AND
  // stop the currently playing AudioBufferSourceNode. Without this the
  // current sentence finishes before the jump takes effect.
  abortServerPlayback();
}

export function getDiag() {
  return {
    device: 'on-device-webgpu' as const,
    ctxState: audioCtx?.state || 'none',
    peak: null as number | null,
    lastError: null as string | null,
    loaded: false,
  };
}
